"use client";

import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Edit,
  Eye,
  Filter,
  Search,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { Badge } from "../../components/ui/badge";
import { Domain, GetAdminDashboard200Response, Job } from "../../swagger";
import { adminDashboardApi, adminJobApi, domainApi } from "../../utils/configuration";
import { useNavigate } from "react-router-dom";


interface Filters {
  search: string;
  status: string;
  experienceLevel: string;
  jobType: string;
  domainId: string;
  categoryId: string;
  subCategoryId: string;
  page: number;
  limit: number;
}

export default function JobsPage() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [stats, setStats] = useState<GetAdminDashboard200Response>({
    totalJobs: 0,
    publishedJobs: 0,
    draftJobs: 0,
    emails: 0,
  });
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const [filters, setFilters] = useState<Filters>({
    search: "",
    status: "all",
    experienceLevel: "all",
    jobType: "all",
    domainId: "all",
    categoryId: "all",
    subCategoryId: "all",
    page: 1,
    limit: 10,
  });

  // Debounced search
  const [searchDebounce, setSearchDebounce] = useState("");

  // Get selected domain and category for dependent dropdowns
  const selectedDomain = domains.find((d) => d.id?.toString() === filters.domainId);
  const selectedCategory = selectedDomain?.Categories?.find((c) => c.id?.toString() === filters.categoryId);

  const navigate = useNavigate()

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchDebounce, page: 1 }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchDebounce]);

  // Reset dependent fields when parent changes
  useEffect(() => {
    setFilters((prev) => ({ 
      ...prev, 
      categoryId: "all",
      subCategoryId: "all",
      page: 1 
    }));
  }, [filters.domainId]);

  useEffect(() => {
    setFilters((prev) => ({ 
      ...prev, 
      subCategoryId: "all",
      page: 1 
    }));
  }, [filters.categoryId]);

  

  // Fetch jobs function
  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const query: any = {
        page: filters.page,
        limit: filters.limit,
      };

      if (filters.search.trim()) {
        query.title = filters.search.trim();
      }
      if (filters.status !== "all") {
        query.status = filters.status;
      }
      if (filters.experienceLevel !== "all") {
        query.experienceLevel = filters.experienceLevel;
      }
      if (filters.jobType !== "all") {
        query.jobType = filters.jobType;
      }
      if (filters.domainId !== "all") {
        query.domainId = filters.domainId;
      }
      if (filters.categoryId !== "all") {
        query.categoryId = filters.categoryId;
      }
      if (filters.subCategoryId !== "all") {
        query.subCategoryId = filters.subCategoryId;
      }

      const data = await adminJobApi.adminGetAllJobs(query);
      if (!data) {
        throw new Error("Failed to fetch jobs");
      }
      setJobs(data.data!);
      setPagination({
        page: data.page!,
        limit: data.limit!,
        total: data.total!,
        totalPages: data.totalPages!,
      });
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch domains
  const fetchDomains = async () => {
    try {
      const fetchDomains = await domainApi.getAllDomains()
      if (!fetchDomains) {
        throw new Error("Failed to fetch domains");
      }
      const data = fetchDomains
      setDomains(data);
    } catch (error) {
      console.error("Error fetching domains:", error);
    }
  };

  // Fetch dashboard stats
  const fetchStats = async () => {
    try {
      const response = await adminDashboardApi.getAdminDashboard()
      setStats(response);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchDomains();
    fetchStats();
  }, []);

  // Fetch jobs when filters change
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "PUBLISHED":
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            Published
          </Badge>
        );
      case "DRAFT":
        return (
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
            Draft
          </Badge>
        );
      case "CLOSED":
        return (
          <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">
            Closed
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getJobTypeLabel = (jobType: string) => {
    const labels = {
      FULLTIME: "Full-time",
      PARTTIME: "Part-time",
      CONTRACT: "Contract",
      FREELANCE: "Freelance",
      INTERNSHIP: "Internship",
    };
    return labels[jobType as keyof typeof labels] || jobType;
  };

  const getExperienceLevelLabel = (level: string) => {
    const labels = {
      ENTRY: "Entry Level (0-2 years)",
      MID: "Mid Level (2-5 years)",
      SENIOR: "Senior Level (5-8 years)",
      LEAD: "Lead Level (8+ years)",
      EXECUTIVE: "Executive Level",
    };
    return labels[level as keyof typeof labels] || level;
  };

  const handleEdit = (jobId: number) => {
        navigate(`/admin/view-job/${jobId}`)

  };

  const handleView = (jobId: number) => {
    navigate(`/admin/view-job/${jobId}`)
  };

  const handleDelete = async (jobId: number) => {
    // if (!confirm("Are you sure you want to delete this job?")) {
    //   return;
    // }

    try {
      const response = await fetch(`/api/job/${jobId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete job");
      }

      // Refetch jobs and stats after deletion
      await fetchJobs();
      await fetchStats();
    } catch (error) {
      console.error("Error deleting job:", error);
      // Handle error - show toast notification
    }
  };

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit: string) => {
    setFilters((prev) => ({ ...prev, limit: parseInt(newLimit), page: 1 }));
  };

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };


  const redirectTo = (path: string) => {
    navigate(`/admin/${path}`)
  };

  return (
    <div className="lg:pl-64 space-y-6 pt-20">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Job Posts</h1>
          <p className="text-gray-600 mt-1">Manage all your job postings</p>
        </div>
        <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => redirectTo('create-job')}>
          Create New Job
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Jobs</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.totalJobs}
                </p>
              </div>
              <div className="h-8 w-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Eye className="h-4 w-4 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Published</p>
                <p className="text-2xl font-bold text-green-600">
                  {stats.publishedJobs}
                </p>
              </div>
              <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center">
                <Eye className="h-4 w-4 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Drafts</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {stats.draftJobs}
                </p>
              </div>
              <div className="h-8 w-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Edit className="h-4 w-4 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Applications
                </p>
                <p className="text-2xl font-bold text-indigo-600">
                  {stats.emails}
                </p>
              </div>
              <div className="h-8 w-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Eye className="h-4 w-4 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2 text-indigo-600" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* First Row of Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search jobs..."
                value={searchDebounce}
                onChange={(e) => setSearchDebounce(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select
              value={filters.status}
              onValueChange={(value) => handleFilterChange("status", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="PUBLISHED">Published</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="CLOSED">Closed</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.experienceLevel}
              onValueChange={(value) =>
                handleFilterChange("experienceLevel", value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="All Experience Levels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Experience Levels</SelectItem>
                <SelectItem value="ENTRY">Entry Level</SelectItem>
                <SelectItem value="MID">Mid Level</SelectItem>
                <SelectItem value="SENIOR">Senior Level</SelectItem>
                <SelectItem value="LEAD">Lead Level</SelectItem>
                <SelectItem value="EXECUTIVE">Executive Level</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.jobType}
              onValueChange={(value) => handleFilterChange("jobType", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Job Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Job Types</SelectItem>
                <SelectItem value="FULLTIME">Full-time</SelectItem>
                <SelectItem value="PARTTIME">Part-time</SelectItem>
                <SelectItem value="CONTRACT">Contract</SelectItem>
                <SelectItem value="FREELANCE">Freelance</SelectItem>
                <SelectItem value="INTERNSHIP">Internship</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Second Row - Category Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Select
                value={filters.domainId}
                onValueChange={(value) => handleFilterChange("domainId", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Domains" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Domains</SelectItem>
                  {domains.map((domain) => (
                    <SelectItem key={domain.id} value={domain.id?.toString() ?? ''}>
                      {domain.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Select
                value={filters.categoryId}
                onValueChange={(value) => handleFilterChange("categoryId", value)}
                disabled={filters.domainId === "all"}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {selectedDomain?.Categories?.map((category) => (
                    <SelectItem key={category.id} value={category.id?.toString() ?? ''}>
                      {category.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Select
                value={filters.subCategoryId}
                onValueChange={(value) => handleFilterChange("subCategoryId", value)}
                disabled={filters.categoryId === "all"}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Subcategories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subcategories</SelectItem>
                  {selectedCategory?.SubCategories?.map((subcategory) => (
                    <SelectItem key={subcategory.id} value={subcategory.id?.toString() ?? ''}>
                      {subcategory.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Jobs Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Job Listings ({pagination.total})</CardTitle>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Show</span>
            <Select
              value={filters.limit.toString()}
              onValueChange={handleLimitChange}
            >
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-gray-600">per page</span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-gray-500">Loading jobs...</div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Job Title</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Applications</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobs.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="text-center py-8 text-gray-500"
                      >
                        No jobs found matching your criteria
                      </TableCell>
                    </TableRow>
                  ) : (
                    jobs.map((job) => (
                      <TableRow key={job.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium text-gray-900">
                              {job.title}
                            </div>
                            <div className="text-sm text-gray-500">
                              {getJobTypeLabel(job.type!)} •{" "}
                              {getExperienceLevelLabel(job.experienceLevel!)}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{job.companyName}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{job.location}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div className="font-medium">{job.Domain?.title}</div>
                            <div className="text-gray-500">
                              {job.Category?.title}
                            </div>
                            {job.SubCategory && (
                              <div className="text-xs text-gray-400">
                                {job.SubCategory.title}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(job.status!)}</TableCell>
                        <TableCell>
                          <div className="font-medium">{job.Emails?.length!}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-gray-500">
                            {new Date(job.createdAt!).toLocaleDateString()}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleView(job.id!)}
                              className="h-8 w-8 p-0"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(job.id!)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(job.id!)}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-600">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
                of {pagination.total} results
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>

                <div className="flex items-center space-x-1">
                  {Array.from(
                    { length: Math.min(5, pagination.totalPages) },
                    (_, i) => {
                      let pageNumber: number;
                      if (pagination.totalPages <= 5) {
                        pageNumber = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNumber = i + 1;
                      } else if (pagination.page >= pagination.totalPages - 2) {
                        pageNumber = pagination.totalPages - 4 + i;
                      } else {
                        pageNumber = pagination.page - 2 + i;
                      }

                      return (
                        <Button
                          key={pageNumber}
                          variant={
                            pagination.page === pageNumber
                              ? "default"
                              : "outline"
                          }
                          size="sm"
                          onClick={() => handlePageChange(pageNumber)}
                          className="w-8 h-8 p-0"
                        >
                          {pageNumber}
                        </Button>
                      );
                    }
                  )}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}